import type { ButtonHTMLAttributes } from 'react';
import { useId, useState } from 'react';
import { variantColors, type ButtonVariant } from '../Button/variantColors';
import { BUTTON_SHADOW_PATH, BUTTON_BORDER_PATH, BUTTON_MAIN_PATH } from '../svg-paths';
import { createResponsiveTextStroke } from '@/styles/textStroke';
import { useClickWithSound } from '@/shared/hooks/useClickWithSound';

export interface NavButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  arrowDirection?: 'left' | 'right' | 'down';
}

function Arrow({ direction }: { direction: 'left' | 'right' | 'down' }) {
  const rotation = {
    right: 0,
    down: 90,
    left: 180
  }[direction];

  return (
    <svg
      className="h-[40%] w-auto"
      viewBox="0 0 40 50"
      fill="none"
      style={{ transform: `rotate(${String(rotation)}deg)` }}
      aria-hidden="true"
    >
      <path d="M5 5L35 25L5 45V5Z" fill="white" />
    </svg>
  );
}

export function NavButton({
  children,
  className = '',
  variant = 'blue',
  arrowDirection = 'right',
  disabled,
  onClick,
  ...props
}: NavButtonProps) {
  const [isPressed, setIsPressed] = useState(false);
  const gradientId = useId();

  const colors = variantColors[variant];
  const activePress = isPressed && !disabled;

  const isLeftArrow = arrowDirection === 'left';
  const handleClick = useClickWithSound(onClick, disabled);

  return (
    <button
      className={`relative transition-all duration-150 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      style={{ containerType: 'size' }}
      onMouseDown={() => {
        setIsPressed(true);
      }}
      onMouseUp={() => {
        setIsPressed(false);
      }}
      onMouseLeave={() => {
        setIsPressed(false);
      }}
      onTouchStart={() => {
        setIsPressed(true);
      }}
      onTouchEnd={() => {
        setIsPressed(false);
      }}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      <svg
        className="block size-full transition-transform duration-150"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 403 106"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
        aria-hidden="true"
      >
        {/* Shadow/base layer */}
        <path d={BUTTON_SHADOW_PATH} fill={colors.shadow} />
        {/* Black border */}
        <path d={BUTTON_BORDER_PATH} fill="#000" />
        {/* Main gradient button */}
        <path d={BUTTON_MAIN_PATH} fill={`url(#${gradientId})`} />
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="201"
            x2="201"
            y1="89"
            y2="4"
          >
            <stop stopColor={colors.gradient.start} />
            <stop offset="0.49" stopColor={colors.gradient.middle} />
            <stop offset="1" stopColor={colors.gradient.end} />
          </linearGradient>
        </defs>
      </svg>

      <span
        className="absolute inset-0 flex items-center justify-center pointer-events-none transition-transform duration-150"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
      >
        {/* Left arrow - positioned at left edge */}
        {isLeftArrow && (
          <span className="absolute left-[5%] h-full flex items-center">
            <Arrow direction="left" />
          </span>
        )}

        {/* Centered text */}
        <span
          className="font-mind-boggle text-[#F2F2F2] leading-none whitespace-nowrap"
          style={{
            fontSize: 'clamp(0.75rem, 12cqw, 2rem)',
            fontWeight: 1000,
            ...createResponsiveTextStroke(1, 0.06)
          }}
        >
          {children}
        </span>

        {/* Right/Down arrow - positioned at right edge */}
        {!isLeftArrow && (
          <span className="absolute right-[5%] h-full flex items-center">
            <Arrow direction={arrowDirection} />
          </span>
        )}
      </span>
    </button>
  );
}

export default NavButton;
